// sfx — fire-and-forget sound effects with variant rotation and
// pitch dithering, so a rapidly-repeated action (5-unit AoE strike,
// spamming reroll) never sounds like the same stamped clip twice.
//
// Two anti-repetition mechanisms (mirrors the reave project):
//   1. VARIANTS — a logical sound maps to N generated files
//      (`<name>-1.mp3` … `<name>-N.mp3`); each play picks a different
//      one than the last.
//   2. PITCH DITHER — each play nudges the playback rate by a small
//      random ± percentage, so even a single-variant sound shimmers.
//
// playSfx() is SAFE to call anywhere: it no-ops before the audio
// context is unlocked (Howler auto-unlocks on first user gesture),
// while muted, and while throttled. Battle code passes opts.gate to
// silence seek/skip/snap jumps and thin out high speeds.

import { Howl, Howler } from 'howler'
import { masterGain, subscribeSoundSettings } from './soundSettings'

export type SfxCategory = 'ui' | 'shop' | 'combine' | 'battle' | 'element' | 'meta'

export interface SfxDef {
  /** Number of `<name>-N.mp3` variant files (≥1). */
  variants: number
  /** Base gain 0..1 (multiplied by master). Default 1. */
  volume?: number
  /** Minimum ms between plays of this sound (anti-machine-gun). */
  throttleMs?: number
  /** ± fractional playback-rate dither. 0.05 = ±5%. Default 0.04. */
  pitch?: number
  category: SfxCategory
  /** Human label for the /?sfx test page. */
  label: string
}

// ---------------------------------------------------------------
// Manifest — the single source of truth for playback AND the test
// page. `variants` reflects how many files have actually been
// generated; sounds not yet generated sit at variants:0 and no-op
// (the test page shows them as "pending").
// ---------------------------------------------------------------
export const SFX: Record<string, SfxDef> = {
  // --- UI -------------------------------------------------------
  'ui-tap':     { variants: 3, category: 'ui', label: 'Tap / select', volume: 0.5, throttleMs: 40, pitch: 0.06 },
  'ui-pickup':  { variants: 2, category: 'ui', label: 'Drag pickup', volume: 0.5, throttleMs: 40, pitch: 0.05 },
  'ui-place':   { variants: 2, category: 'ui', label: 'Drop / place', volume: 0.55, throttleMs: 40, pitch: 0.05 },

  // --- Shop -----------------------------------------------------
  'shop-buy':    { variants: 2, category: 'shop', label: 'Buy unit', volume: 0.7 },
  'shop-sell':   { variants: 2, category: 'shop', label: 'Sell (coins)', volume: 0.7 },
  'shop-reroll': { variants: 2, category: 'shop', label: 'Reroll', volume: 0.65 },
  'shop-lock':   { variants: 1, category: 'shop', label: 'Lock toggle', volume: 0.6, pitch: 0.05 },
  'shop-ready':  { variants: 1, category: 'shop', label: 'Ready (drum)', volume: 0.8 },

  // --- Combine --------------------------------------------------
  'combine-tick':    { variants: 2, category: 'combine', label: 'Fill tick', volume: 0.7 },
  'combine-slam':    { variants: 2, category: 'combine', label: 'Card slam', volume: 0.85 },
  'combine-fold':    { variants: 1, category: 'combine', label: 'Unfold sequence', volume: 0.7 },
  'combine-stamp':   { variants: 1, category: 'combine', label: 'Evolution stamp', volume: 0.85 },
  'combine-sparkle': { variants: 1, category: 'combine', label: 'Reveal sparkle', volume: 0.7 },

  // --- Battle ---------------------------------------------------
  'battle-strike':  { variants: 3, category: 'battle', label: 'Strike', volume: 0.6, throttleMs: 55, pitch: 0.07 },
  'battle-crumple': { variants: 2, category: 'battle', label: 'Death (crumple)', volume: 0.7, throttleMs: 60, pitch: 0.05 },
  'battle-victory': { variants: 1, category: 'battle', label: 'Victory fanfare', volume: 0.8 },
  'battle-start':   { variants: 1, category: 'battle', label: 'Battle start (gong)', volume: 0.75 },
  'hit-super':      { variants: 2, category: 'battle', label: 'Super-effective', volume: 0.7, throttleMs: 55, pitch: 0.06 },
  'hit-resist':     { variants: 2, category: 'battle', label: 'Resisted', volume: 0.6, throttleMs: 55, pitch: 0.05 },
  'cast-generic':   { variants: 2, category: 'battle', label: 'Cast ability', volume: 0.6, throttleMs: 50 },
  'heal':           { variants: 2, category: 'battle', label: 'Heal', volume: 0.6, throttleMs: 50, pitch: 0.05 },
  'buff-up':        { variants: 2, category: 'battle', label: 'Buff up', volume: 0.55, throttleMs: 45, pitch: 0.06 },
  'buff-down':      { variants: 2, category: 'battle', label: 'Buff down', volume: 0.55, throttleMs: 45, pitch: 0.06 },
  'summon-unfold':  { variants: 2, category: 'battle', label: 'Summon unfold', volume: 0.6, throttleMs: 50 },
  'synergy-stamp':  { variants: 1, category: 'battle', label: 'Synergy banner', volume: 0.6 },

  // --- Element casts -------------------------------------------
  'el-fire':  { variants: 1, category: 'element', label: 'Fire cast', volume: 0.65, throttleMs: 50 },
  'el-water': { variants: 1, category: 'element', label: 'Water cast', volume: 0.65, throttleMs: 50 },
  'el-wind':  { variants: 1, category: 'element', label: 'Wind cast', volume: 0.65, throttleMs: 50 },
  'el-wood':  { variants: 1, category: 'element', label: 'Wood cast', volume: 0.65, throttleMs: 50 },
  'el-earth': { variants: 1, category: 'element', label: 'Earth cast', volume: 0.65, throttleMs: 50 },

  // --- Meta -----------------------------------------------------
  'title-cta':  { variants: 2, category: 'meta', label: 'Title CTA', volume: 0.6 },
  'lobby-join': { variants: 1, category: 'meta', label: 'Lobby join', volume: 0.6 },
  'inscribe':   { variants: 1, category: 'meta', label: 'Player inscribe', volume: 0.7 },
  'gold-earn':  { variants: 1, category: 'meta', label: 'Gold earned', volume: 0.6 },
  'defeat':     { variants: 1, category: 'meta', label: 'Defeat', volume: 0.7 },
}

export type SfxName = keyof typeof SFX

const DEFAULT_PITCH = 0.04

interface LoadedSfx {
  howls: Howl[]
  available: boolean[]
  lastVariant: number
  lastPlayedAt: number
}

const loaded = new Map<string, LoadedSfx>()

/** Keep Howler's global volume in sync with the settings store so a
 *  live volume/mute change applies to in-flight and future sounds. */
let synced = false
function ensureSynced(): void {
  if (synced || typeof window === 'undefined') return
  synced = true
  const apply = () => Howler.volume(masterGain())
  apply()
  subscribeSoundSettings(apply)
}

function variantPath(name: string, n: number): string {
  return `${import.meta.env.BASE_URL}sfx/${name}-${n}.mp3`
}

function ensureLoaded(name: string): LoadedSfx | null {
  const def = SFX[name]
  if (!def || def.variants < 1) return null
  let entry = loaded.get(name)
  if (entry) return entry
  const howls: Howl[] = []
  const available: boolean[] = []
  for (let i = 1; i <= def.variants; i++) {
    const idx = i - 1
    available[idx] = true
    const howl = new Howl({
      src: [variantPath(name, i)],
      volume: def.volume ?? 1,
      preload: true,
      html5: false, // WebAudio: low latency + per-id rate control
      onloaderror: () => {
        available[idx] = false
      },
    })
    howls.push(howl)
  }
  entry = { howls, available, lastVariant: -1, lastPlayedAt: 0 }
  loaded.set(name, entry)
  return entry
}

export interface PlaySfxOpts {
  /** Extra gain multiplier for this play (e.g. battle speed gating). */
  volume?: number
  /** Override the dithered rate entirely. */
  rate?: number
  /** Skip the per-sound throttle (one-shot ceremony beats). */
  ignoreThrottle?: boolean
  /** When false, the call no-ops — used to silence seek/skip/snap
   *  timeline jumps and thin out high replay speeds at the call site. */
  gate?: boolean
}

/** Play a sound. Fire-and-forget; safe to call anywhere. */
export function playSfx(name: SfxName, opts: PlaySfxOpts = {}): void {
  if (opts.gate === false) return
  if (typeof window === 'undefined') return
  if (masterGain() <= 0) return
  ensureSynced()

  const def = SFX[name]
  const entry = ensureLoaded(name)
  if (!def || !entry) return

  const now = performance.now()
  const throttle = def.throttleMs ?? 0
  if (!opts.ignoreThrottle && throttle > 0 && now - entry.lastPlayedAt < throttle) {
    return
  }

  // Pick a variant that isn't the one we just played (when possible)
  // and is actually loaded.
  const usable = entry.howls
    .map((_, i) => i)
    .filter((i) => entry.available[i])
  if (usable.length === 0) return
  let pick = usable[Math.floor(Math.random() * usable.length)]!
  if (usable.length > 1 && pick === entry.lastVariant) {
    // rotate to the next usable variant
    const k = (usable.indexOf(pick) + 1) % usable.length
    pick = usable[k]!
  }
  entry.lastVariant = pick
  entry.lastPlayedAt = now

  const howl = entry.howls[pick]!
  const id = howl.play()
  // Per-play pitch dither (or explicit override).
  const pitch = def.pitch ?? DEFAULT_PITCH
  const rate = opts.rate ?? 1 + (Math.random() * 2 - 1) * pitch
  howl.rate(rate, id)
  if (opts.volume !== undefined) {
    howl.volume((def.volume ?? 1) * opts.volume, id)
  }
}

/** Play ONE specific variant (1-based) at its raw pitch — for the
 *  /?sfx test bench so each generated file can be auditioned and
 *  flagged for regeneration individually. Ignores throttle/dither. */
export function playSfxVariant(name: SfxName, variant1Based: number): void {
  if (typeof window === 'undefined') return
  if (masterGain() <= 0) return
  ensureSynced()
  const entry = ensureLoaded(name)
  if (!entry) return
  const idx = variant1Based - 1
  const howl = entry.howls[idx]
  if (!howl) return
  const id = howl.play()
  howl.rate(1, id) // raw — no dither, so the actual sample is judged
}

/** Eagerly load a set of sounds (battle / shop mount) so the first
 *  play has no fetch latency. No-ops for not-yet-generated sounds. */
export function preloadSfx(names: SfxName[]): void {
  ensureSynced()
  for (const n of names) ensureLoaded(n)
}

/** Load every generated sound — used by the /?sfx test page. */
export function preloadAllSfx(): void {
  ensureSynced()
  for (const name of Object.keys(SFX)) ensureLoaded(name)
}

/** Whether at least one variant of a sound has loaded successfully —
 *  drives the test page's availability badges. */
export function isSfxAvailable(name: SfxName): boolean {
  const entry = loaded.get(name)
  return entry ? entry.available.some(Boolean) : (SFX[name]?.variants ?? 0) > 0
}
